"use client";

import { c as _c } from "react/compiler-runtime";
import { Button, Link, Spinner, Tooltip } from "@dc/ui/components";
import { cn } from "@dc/ui/lib/utils";
import { Wordmark } from "@dc/ui/vectors";
import { useTranslations } from "next-intl";
import type { ReactNode } from "react";
import { HamburgerMenuLinear, MagniferLinear } from "solar-icon-set";
import { GlobalSearch } from "@/components/GlobalSearch/GlobalSearch";
import { prefocusForKeyboard, useGlobalSearch } from "@/components/GlobalSearch/GlobalSearch.hooks";
import { NotificationDrawer } from "@/components/NotificationDrawer";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useIOSKeyboardScrollFix } from "@/hooks/useIOSKeyboardScrollFix";
import { useScrollToHighlight } from "@/hooks/useScrollToHighlight";
import { useTeamsContext } from "@/hooks/useTeamsContext";
import { NavigationSidebar, useNavigationSidebar } from "../NavigationSidebar";
import { useNavigationItems } from "../Sidebar/navigationItems/useNavigationItems";
import type { SidebarItem } from "../Sidebar/Sidebar.types";
const DEFAULT_ICON_COLOR = "text-zinc-300";
export const AppLayoutClient = t0 => {
  const $ = _c(60);
  const {
    children,
    navigationItems: navigationItemsProp,
    selectedKey: propSelectedKey,
    backgroundTile,
    shouldCollapseNavOnMount: t1,
    showMenuButton: t2
  } = t0;
  const shouldCollapseNavOnMount = t1 === undefined ? false : t1;
  const showMenuButton = t2 === undefined ? true : t2;
  const t = useTranslations("navigation");
  const tCommon = useTranslations();
  const {
    defaultNavItems
  } = useNavigationItems();
  const navigationItems = navigationItemsProp ?? defaultNavItems;
  let t3;
  if ($[0] !== navigationItems || $[1] !== propSelectedKey || $[2] !== shouldCollapseNavOnMount) {
    t3 = {
      navigationItems,
      selectedKey: propSelectedKey,
      shouldCollapseNavOnMount
    };
    $[0] = navigationItems;
    $[1] = propSelectedKey;
    $[2] = shouldCollapseNavOnMount;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const sidebarState = useNavigationSidebar(t3);
  const flags = useFeatureFlags();
  const showSearch = flags["sidebar-show-search"];
  const globalSearch = useGlobalSearch();
  const {
    isSwitchingTeam
  } = useTeamsContext();
  const {
    isCollapsed,
    onOpenChange
  } = sidebarState;
  useIOSKeyboardScrollFix();
  useScrollToHighlight();
  let t4;
  if ($[4] !== backgroundTile) {
    t4 = backgroundTile ? {
      backgroundImage: `url('${backgroundTile}')`,
      backgroundRepeat: "repeat",
      backgroundPosition: "center"
    } : undefined;
    $[4] = backgroundTile;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t) {
    t5 = t("shell.main_navigation");
    $[6] = t;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== globalSearch.onOpen || $[9] !== globalSearch.shortcutLabel || $[10] !== sidebarState) {
    t6 = <NavigationSidebar state={sidebarState} onNavigate={sidebarState.onClose} onSearchOpen={globalSearch.onOpen} searchShortcutLabel={globalSearch.shortcutLabel} />;
    $[8] = globalSearch.onOpen;
    $[9] = globalSearch.shortcutLabel;
    $[10] = sidebarState;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5 || $[13] !== t6) {
    t7 = <nav aria-label={t5}>{t6}</nav>;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = cn("flex h-full w-full flex-col overflow-hidden p-0 sm:px-3 sm:pt-3");
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== onOpenChange || $[17] !== showMenuButton || $[18] !== t) {
    t9 = showMenuButton ? <Tooltip content={t("actions.open_menu")} placement="bottom" color="foreground"><Button isIconOnly={true} variant="light" className="size-10 rounded-xl text-white! hover:bg-navy-700! hover:text-white!" onPress={onOpenChange} aria-label={t("actions.open_menu")} startContent={<HamburgerMenuLinear size={20} className={DEFAULT_ICON_COLOR} />} /></Tooltip> : <div className="size-10" />;
    $[16] = onOpenChange;
    $[17] = showMenuButton;
    $[18] = t;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t) {
    t10 = t("shell.home");
    $[20] = t;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Wordmark className="h-5 w-[136px] text-white" />;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10) {
    t12 = <div className="flex flex-1 items-center justify-center px-2"><Link href="/" className="flex items-center" aria-label={t10}>{t11}</Link></div>;
    $[23] = t10;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== globalSearch.onOpen || $[26] !== globalSearch.shortcutLabel || $[27] !== showSearch || $[28] !== t || $[29] !== tCommon) {
    t13 = showSearch ? <div className="flex items-center gap-2"><Tooltip content={t("shell.search_with_shortcut", {
        shortcut: globalSearch.shortcutLabel
      })} placement="bottom" color="foreground"><button type="button" aria-label={tCommon("search")} className="inline-flex size-10 items-center justify-center rounded-xl text-white! transition-colors hover:bg-navy-700!" onPointerDown={prefocusForKeyboard} onClick={globalSearch.onOpen}><MagniferLinear size={20} className={DEFAULT_ICON_COLOR} /></button></Tooltip></div> : <div className="size-10" />;
    $[25] = globalSearch.onOpen;
    $[26] = globalSearch.shortcutLabel;
    $[27] = showSearch;
    $[28] = t;
    $[29] = tCommon;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t12 || $[32] !== t13 || $[33] !== t9) {
    t14 = <header className="flex h-14 w-full shrink-0 items-center justify-between rounded-large bg-navy-800 px-4 py-2 sm:hidden">{t9}{t12}{t13}</header>;
    $[31] = t12;
    $[32] = t13;
    $[33] = t9;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== children) {
    t15 = <div className="flex min-h-0 flex-1 flex-col overflow-auto" data-testid="app-layout-content" tabIndex={0}>{children}</div>;
    $[35] = children;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  const t16 = isSwitchingTeam ? "opacity-100" : "pointer-events-none opacity-0";
  let t17;
  if ($[37] !== t16) {
    t17 = cn("absolute inset-0 z-50 flex items-center justify-center bg-background/80 transition-opacity duration-200", t16);
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Spinner size="lg" />;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t17) {
    t19 = <div className={t17} data-testid="team-switch-overlay">{t18}</div>;
    $[40] = t17;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== t15 || $[43] !== t19) {
    t20 = <div className="relative ml-0 flex min-h-0 flex-1 flex-col gap-0 overflow-hidden bg-background p-0 sm:rounded-t-large">{t15}{t19}</div>;
    $[42] = t15;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== t14 || $[46] !== t20) {
    t21 = <main id="main-content" className={t8}>{t14}{t20}</main>;
    $[45] = t14;
    $[46] = t20;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== isCollapsed) {
    t22 = <NotificationDrawer isCollapsed={isCollapsed} />;
    $[48] = isCollapsed;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== globalSearch.isOpen || $[51] !== globalSearch.onOpenChange || $[52] !== showSearch) {
    t23 = showSearch && <GlobalSearch isOpen={globalSearch.isOpen} onOpenChange={globalSearch.onOpenChange} />;
    $[50] = globalSearch.isOpen;
    $[51] = globalSearch.onOpenChange;
    $[52] = showSearch;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== t21 || $[55] !== t22 || $[56] !== t23 || $[57] !== t4 || $[58] !== t7) {
    t24 = <div className="flex h-(--app-height) min-h-0 w-full flex-1 bg-bg-container-page" style={t4}>{t7}{t21}{t22}{t23}</div>;
    $[54] = t21;
    $[55] = t22;
    $[56] = t23;
    $[57] = t4;
    $[58] = t7;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  return t24;
};